import { createEmitter } from "../Utilities/events";
import { PlayerEvent } from "../Core/PlayerEvents";

/**
 * Common text + audio track façade.
 * Independent of streaming provider; uses HTMLMediaElement APIs where available.
 * Future formats (TTML, SRT→VTT) plug in via parsers without changing the engine.
 */

/**
 * @param {object} [options]
 */
export function createTrackController(options) {
  options = options || {};
  var emitter = createEmitter();
  var video = null;
  var textTracks = []; // normalized
  var audioTracks = [];
  var activeTextId = null;
  var activeAudioId = null;
  var trackEls = [];
  var objectUrls = [];
  var onAddTrack = null;

  function emitText() {
    emitter.emit(PlayerEvent.TEXT_TRACKS_CHANGED, {
      tracks: getTextTracks(),
      activeId: activeTextId,
    });
  }

  function emitAudio() {
    emitter.emit(PlayerEvent.AUDIO_TRACKS_CHANGED, {
      tracks: getAudioTracks(),
      activeId: activeAudioId,
    });
  }

  function clearTextDom() {
    trackEls.forEach(function (el) {
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
    trackEls = [];
    objectUrls.forEach(function (url) {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        /* ignore */
      }
    });
    objectUrls = [];
  }

  /**
   * Mirror native / HLS text tracks into the UI list and force OFF by default.
   * Live HLS often ships embedded captions that show even when the menu says Off.
   */
  function syncNativeTextTracks(opts) {
    opts = opts || {};
    var keepActive = !!opts.keepActive;
    if (!video || !video.textTracks) {
      return;
    }
    var managedIds = {};
    trackEls.forEach(function (el) {
      var mid = el && el.getAttribute("data-ngp-id");
      if (mid) managedIds[mid] = true;
    });

    // Drop prior native entries from the catalog (rebuilt below).
    textTracks = textTracks.filter(function (t) {
      return t.source !== "native";
    });

    for (var i = 0; i < video.textTracks.length; i++) {
      var tt = video.textTracks[i];
      var kind = (tt.kind || "").toLowerCase();
      if (kind && kind !== "subtitles" && kind !== "captions") {
        continue;
      }
      // Skip tracks we already manage via <track data-ngp-id>.
      var matchedManaged = false;
      for (var m = 0; m < trackEls.length; m++) {
        if (trackEls[m] && video.textTracks[i] === trackEls[m].track) {
          matchedManaged = true;
          break;
        }
      }
      if (matchedManaged) {
        continue;
      }
      var nid = "native-" + i;
      textTracks.push({
        id: nid,
        label: tt.label || tt.language || "Captions " + (i + 1),
        language: tt.language || "",
        kind: tt.kind || "subtitles",
        mode: "disabled",
        format: "native",
        source: "native",
        nativeIndex: i,
      });
      if (!keepActive || activeTextId !== nid) {
        try {
          tt.mode = "disabled";
        } catch (e) {
          /* ignore */
        }
      }
    }

    if (!keepActive || !activeTextId) {
      activeTextId = null;
      setTextTracksMode("disabled");
    }
    emitText();
  }

  /**
   * @param {HTMLVideoElement} el
   */
  function attach(el) {
    if (video && video.textTracks && onAddTrack) {
      try {
        video.textTracks.removeEventListener("addtrack", onAddTrack);
      } catch (e) {
        /* ignore */
      }
    }
    video = el;
    onAddTrack = function () {
      // HLS may add caption tracks after MANIFEST_PARSED — keep them OFF.
      syncNativeTextTracks({ keepActive: !!activeTextId });
      if (!activeTextId) {
        setTextTracksMode("disabled");
      }
    };
    if (video && video.textTracks && video.textTracks.addEventListener) {
      video.textTracks.addEventListener("addtrack", onAddTrack);
    }
    syncNativeTextTracks({ keepActive: false });
  }

  /**
   * Load WebVTT (or future-parsed) subtitle descriptors onto the media element.
   * @param {Array<{id?:string,src?:string,content?:string,label?:string,language?:string,kind?:string,default?:boolean}>} list
   */
  function setTextTracks(list) {
    clearTextDom();
    textTracks = textTracks.filter(function (t) {
      return t.source === "native";
    });
    activeTextId = null;
    if (!video) {
      return;
    }

    (list || []).forEach(function (item, index) {
      var id = item.id || "text-" + index;
      var src = item.src;
      if (!src && item.content) {
        var blob = new Blob([item.content], { type: "text/vtt" });
        src = URL.createObjectURL(blob);
        objectUrls.push(src);
      }
      if (!src) {
        return;
      }

      var trackEl = document.createElement("track");
      trackEl.kind = item.kind || "subtitles";
      trackEl.label = item.label || item.language || "Track " + (index + 1);
      trackEl.srclang = item.language || "en";
      trackEl.src = src;
      trackEl.setAttribute("data-ngp-id", id);
      if (item.default) {
        trackEl.default = true;
      }
      video.appendChild(trackEl);
      trackEls.push(trackEl);

      textTracks.push({
        id: id,
        label: trackEl.label,
        language: trackEl.srclang,
        kind: trackEl.kind,
        mode: "disabled",
        format: item.format || "webvtt",
        source: "plugin",
      });
    });

    // Apply default after tracks exist in the list
    var defaultItem = null;
    for (var i = 0; i < (list || []).length; i++) {
      if (list[i].default) {
        defaultItem = list[i];
        break;
      }
    }
    if (defaultItem) {
      selectTextTrack(defaultItem.id || "text-" + (list || []).indexOf(defaultItem));
    } else {
      setTextTracksMode("disabled");
      syncNativeTextTracks({ keepActive: false });
    }
    emitText();
  }

  function setTextTracksMode(mode) {
    if (!video || !video.textTracks) {
      return;
    }
    for (var i = 0; i < video.textTracks.length; i++) {
      try {
        video.textTracks[i].mode = mode;
      } catch (e) {
        /* ignore */
      }
    }
  }

  function selectTextTrack(id) {
    if (!video || !video.textTracks) {
      return;
    }
    if (id == null || id === "off" || id === false) {
      activeTextId = null;
      setTextTracksMode("disabled");
      textTracks.forEach(function (t) {
        t.mode = "disabled";
      });
      emitText();
      emitter.emit(PlayerEvent.TEXT_TRACK_SELECTED, { id: null });
      return;
    }

    var selected = null;
    for (var s = 0; s < textTracks.length; s++) {
      if (textTracks[s].id === id) {
        selected = textTracks[s];
        break;
      }
    }
    if (!selected) {
      // Do not pretend captions enabled when no matching track exists.
      activeTextId = null;
      setTextTracksMode("disabled");
      textTracks.forEach(function (t) {
        t.mode = "disabled";
      });
      emitText();
      emitter.emit(PlayerEvent.TEXT_TRACK_SELECTED, { id: null });
      return;
    }

    activeTextId = id;

    // Disable everything first (OFF is the baseline).
    setTextTracksMode("disabled");
    textTracks.forEach(function (t) {
      t.mode = "disabled";
    });

    if (selected.source === "provider") {
      // HLS/DASH provider tracks — display toggled via setProviderSubtitles.
      selected.mode = "showing";
      emitText();
      emitter.emit(PlayerEvent.TEXT_TRACK_SELECTED, { id: activeTextId });
      return;
    }

    if (selected && selected.source === "native" && selected.nativeIndex != null) {
      var ntt = video.textTracks[selected.nativeIndex];
      if (ntt) {
        try {
          ntt.mode = "showing";
        } catch (e) {
          /* ignore */
        }
        selected.mode = "showing";
      }
    } else {
      for (var j = 0; j < trackEls.length; j++) {
        var el = trackEls[j];
        var trackId = el && el.getAttribute("data-ngp-id");
        if (trackId !== id) {
          continue;
        }
        // Prefer the TextTrack linked to our <track> element.
        var linked = el.track;
        if (linked) {
          try {
            linked.mode = "showing";
          } catch (e2) {
            /* ignore */
          }
        } else if (video.textTracks[j]) {
          try {
            video.textTracks[j].mode = "showing";
          } catch (e3) {
            /* ignore */
          }
        }
        for (var k = 0; k < textTracks.length; k++) {
          if (textTracks[k].id === id) {
            textTracks[k].mode = "showing";
          }
        }
      }
    }

    emitText();
    emitter.emit(PlayerEvent.TEXT_TRACK_SELECTED, { id: activeTextId });
  }

  function getTextTracks() {
    return textTracks.map(function (t) {
      return Object.assign({}, t, {
        active: t.id === activeTextId,
      });
    });
  }

  /**
   * Merge HLS/DASH provider subtitle catalog into the UI list (default OFF).
   * @param {Array<{id?:string,label?:string,language?:string,index?:number}>} list
   */
  function mergeProviderSubtitleTracks(list) {
    textTracks = textTracks.filter(function (t) {
      return t.source !== "provider";
    });
    (list || []).forEach(function (item, index) {
      var id = (item && item.id) || "hls-sub-" + index;
      var exists = textTracks.some(function (t) {
        return t.id === id;
      });
      if (exists) {
        return;
      }
      textTracks.push({
        id: id,
        label:
          (item && (item.label || item.language)) || "Captions " + (index + 1),
        language: (item && item.language) || "",
        kind: "subtitles",
        mode: "disabled",
        format: "provider",
        source: "provider",
        providerIndex: item && item.index != null ? item.index : index,
      });
    });
    if (activeTextId) {
      var stillThere = textTracks.some(function (t) {
        return t.id === activeTextId;
      });
      if (!stillThere) {
        activeTextId = null;
      }
    }
    emitText();
  }

  /**
   * Refresh audio track list from native AudioTrackList + config metadata.
   * @param {Array} [configured]
   */
  function setAudioTracks(configured) {
    audioTracks = [];
    activeAudioId = null;

    var native = video && video.audioTracks;
    if (native && native.length) {
      for (var i = 0; i < native.length; i++) {
        var at = native[i];
        var nid = "native-" + i;
        audioTracks.push({
          id: nid,
          label: at.label || at.language || "Audio " + (i + 1),
          language: at.language || "",
          kind: at.kind || "main",
          source: "native",
          enabled: !!at.enabled,
        });
        if (at.enabled) {
          activeAudioId = nid;
        }
      }
    }

    (configured || []).forEach(function (item, index) {
      var id = item.id || "cfg-" + index;
      var exists = audioTracks.some(function (t) {
        return t.id === id;
      });
      if (exists) {
        return;
      }
      audioTracks.push({
        id: id,
        label: item.label || item.language || "Track " + (index + 1),
        language: item.language || "",
        kind: item.kind || "main",
        source: item.source || "config",
        enabled: !!item.default,
        // Future: alternate audio URL for MSE/provider switching
        src: item.src || null,
        hlsIndex: item.hlsIndex != null ? item.hlsIndex : null,
      });
      if (item.default && !activeAudioId) {
        activeAudioId = id;
      }
    });

    if (!activeAudioId && audioTracks.length) {
      activeAudioId = audioTracks[0].id;
      audioTracks[0].enabled = true;
    }
    emitAudio();
  }

  function selectAudioTrack(id) {
    if (!id) {
      return;
    }
    activeAudioId = id;
    var native = video && video.audioTracks;

    audioTracks.forEach(function (t, index) {
      t.enabled = t.id === id;
      if (t.source === "native" && native) {
        var ni = parseInt(String(t.id).replace("native-", ""), 10);
        if (native[ni]) {
          native[ni].enabled = t.id === id;
        }
      }
    });

    emitAudio();
    emitter.emit(PlayerEvent.AUDIO_TRACK_SELECTED, {
      id: activeAudioId,
      track: getAudioTracks().filter(function (t) {
        return t.id === activeAudioId;
      })[0] || null,
    });
  }

  function getAudioTracks() {
    return audioTracks.map(function (t) {
      return Object.assign({}, t, {
        active: t.id === activeAudioId,
      });
    });
  }

  /**
   * Apply ::cue styles from theme.subtitles
   * @param {HTMLElement} shell
   * @param {object} style
   */
  function applySubtitleTheme(shell, style) {
    style = style || {};
    if (!shell) {
      return;
    }
    shell.style.setProperty(
      "--ngp-cue-color",
      style.color || "#ffffff"
    );
    shell.style.setProperty(
      "--ngp-cue-bg",
      style.background || "rgba(0,0,0,0.65)"
    );
    shell.style.setProperty(
      "--ngp-cue-size",
      style.fontSize || "1.05em"
    );
    shell.style.setProperty(
      "--ngp-cue-font",
      style.fontFamily || "inherit"
    );
  }

  function destroy() {
    if (video && video.textTracks && onAddTrack) {
      try {
        video.textTracks.removeEventListener("addtrack", onAddTrack);
      } catch (e) {
        /* ignore */
      }
    }
    onAddTrack = null;
    clearTextDom();
    textTracks = [];
    audioTracks = [];
    video = null;
    emitter.removeAllListeners();
  }

  return {
    attach: attach,
    setTextTracks: setTextTracks,
    selectTextTrack: selectTextTrack,
    syncNativeTextTracks: syncNativeTextTracks,
    mergeProviderSubtitleTracks: mergeProviderSubtitleTracks,
    getTextTracks: getTextTracks,
    getActiveTextTrackId: function () {
      return activeTextId;
    },
    setAudioTracks: setAudioTracks,
    selectAudioTrack: selectAudioTrack,
    getAudioTracks: getAudioTracks,
    getActiveAudioTrackId: function () {
      return activeAudioId;
    },
    applySubtitleTheme: applySubtitleTheme,
    on: emitter.on,
    off: emitter.off,
    destroy: destroy,
  };
}

export default createTrackController;
